/*
  Shipping calculator — shared implementation.

  This logic used to live inline in ctx-product-details-tabs.liquid, and the
  product page wanted the same thing under the buy button. Two copies of a
  five-step cart-mutating flow would have drifted the first time either was
  touched, so it lives here and both call it.

  Why it is more involved than it looks: Shopify will only quote rates for a
  cart, so the flow has to add the item, ask for rates, poll until the carrier
  answers, then put the cart back exactly as it was. The restore runs in a
  finally block - a customer must never end up with a phantom line item because
  a quote failed.

  Markup class names are prefixed so a caller can keep its own styling. The tab
  section uses the default 'pd' prefix, which is what its existing CSS targets.
*/
(function () {
  var POLL_ATTEMPTS = 20;
  var POLL_INTERVAL = 700;
  var DEBUG = false;

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[ctx shipping calc]');
    console.log.apply(console, args);
  }

  function wait(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  // async_shipping_rates returns price as a major-units string, e.g. "49.00".
  function money(p) {
    var n = parseFloat(p);
    return isNaN(n) ? '' : '$' + n.toFixed(2);
  }

  function getVariantId() {
    var el = document.querySelector('product-form [name="id"]') ||
             document.querySelector('form[action*="/cart/add"] [name="id"]') ||
             document.querySelector('#cp-variant-id');
    return el ? el.value : null;
  }

  function getQty() {
    var el = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
    var n = el ? parseInt(el.value, 10) : 1;
    return (n && n > 0) ? n : 1;
  }

  // Fetch that never hides an error: returns status + parsed body + raw text.
  async function fetchJson(url, opts) {
    var res = await fetch(url, opts);
    var text = await res.text();
    var body = null;
    try { body = text ? JSON.parse(text) : null; } catch (e) { /* not JSON */ }
    return { ok: res.ok, status: res.status, body: body, raw: text, url: url };
  }

  function errorText(payload) {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (payload.description) return payload.description;
    if (payload.message) return payload.message;
    if (payload.errors) {
      try { return JSON.stringify(payload.errors); } catch (e) { return 'error'; }
    }
    return '';
  }

  /*
    Flag the rate list only when it actually overflows.

    The bottom fade and the scrollbar gutter are the "there is more below" cue, so
    they must not appear on a list that is already fully visible - a fade over the
    last row of a complete list reads as clipped content that cannot be reached.
    Measured rather than inferred from the number of rates: row height varies with
    whether a rate carries a description line, and the same list overflows on a
    phone but not on a desktop.

    Re-checked on resize because the pop-out's height is viewport-relative, so
    rotating a phone can change whether the list fits without the list changing.
  */
  function markScrollable(box) {
    if (!box) return;

    var check = function () {
      /* 2px tolerance: sub-pixel layout rounding can report a 1px overflow on a
         list that visually fits, which would fade the last row for no reason. */
      var overflows = box.scrollHeight - box.clientHeight > 2;
      box.classList.toggle('is-scrollable', overflows);
    };

    /* After layout - called straight from innerHTML, clientHeight is not final yet. */
    if (window.requestAnimationFrame) window.requestAnimationFrame(check);
    else check();

    window.addEventListener('resize', check);
    if (window.ResizeObserver) new ResizeObserver(check).observe(box);
  }

  function renderRates(result, rates, p) {
    log('rates returned:', rates);

    if (!rates || !rates.length) {
      result.textContent = 'No shipping options returned for that address.';
      return;
    }

    /* Cheapest first.

       Shopify returns rates in shipping-profile order, which put a $447 white glove
       rate above cheaper options and buried the rest. Sorted on a COPY so the
       caller's array is untouched, and on parseFloat because price is a string
       ("49.00") - sorting those as strings puts "150.00" before "49.00". */
    var sorted = rates.slice().sort(function (a, b) {
      return parseFloat(a.price) - parseFloat(b.price);
    });

    /* Assembly used to be a product option with its own description beside it.
       It is a shipping rate now, so without this a customer sees two freight
       options a hundred dollars apart with nothing saying what the difference
       buys. ctxRateNote supplies that line, matched on the rate name. */
    var rows = sorted.map(function (r) {
      var note = window.ctxRateNote ? window.ctxRateNote(r.name) : '';
      return '<div class="' + p + '-rate-row"><span>' + r.name + '</span><strong>' + money(r.price) + '</strong></div>' +
             (note ? '<div class="' + p + '-rate-what">' + note + '</div>' : '');
    }).join('');

    /* The rate list scrolls inside a fixed-height window rather than growing the
       card. Long lists used to push the footer note past the bottom of the pop-out,
       so the last rates were unreachable on a short screen. The note stays outside
       the scroller so it is always visible. */
    result.innerHTML =
      '<div class="' + p + '-rates" tabindex="0" role="group" aria-label="Shipping rates">' +
        rows +
      '</div>' +
      '<span class="' + p + '-rate-note">Estimate. Final rate is confirmed at checkout.</span>';

    markScrollable(result.querySelector('.' + p + '-rates'));
  }

  /*
    ─────────────────────────────────────────────────────────────────────────────
    Quoting THIS item only, while the customer may have a cart.

    Shopify has no per-item rate endpoint - prepare_shipping_rates.json always
    prices the whole session cart. So to quote one machine the cart is emptied,
    the item is added alone, rates are read, and the original cart is put back.

    What makes that safe enough to do:

    - The snapshot is written to sessionStorage BEFORE anything is emptied, and
      restored on the next page load if this tab dies mid-quote. Without that, a
      crash or a closed tab during a two-second window would lose someone's cart.
    - Line item properties are preserved. They are not cosmetic here: the bundle
      discount Shopify Function keys off _bundle_id, so dropping properties would
      silently cost the customer their 5%.
    - selling_plan_id is preserved for subscription lines.
    - The cart note and attributes are snapshotted and restored too; clear.js
      drops the items but these are separate state.
    - If the snapshot itself fails, the cart is NOT emptied - it falls back to
      quoting the cart as-is rather than risking data it could not record.
    ─────────────────────────────────────────────────────────────────────────────
  */
  var SNAPSHOT_KEY = 'ctxCalcCartSnapshot';

  function snapshotCart(cart) {
    return {
      items: (cart.items || []).map(function (it) {
        return {
          id: it.variant_id || it.id,
          quantity: it.quantity,
          properties: it.properties && Object.keys(it.properties).length ? it.properties : null,
          selling_plan_id: (it.selling_plan_allocation &&
                            it.selling_plan_allocation.selling_plan &&
                            it.selling_plan_allocation.selling_plan.id) || null
        };
      }),
      note: cart.note || null,
      attributes: cart.attributes && Object.keys(cart.attributes).length ? cart.attributes : null
    };
  }

  async function restoreCart(snap) {
    if (!snap) return;

    await fetchJson('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    });

    /* Added one at a time, in the original order. add.js accepts an items array,
       but one failing line would reject the whole batch and lose the rest. */
    for (var i = 0; i < snap.items.length; i++) {
      var it = snap.items[i];
      var payload = { id: it.id, quantity: it.quantity };
      if (it.properties) payload.properties = it.properties;
      if (it.selling_plan_id) payload.selling_plan_id = it.selling_plan_id;
      try {
        await fetchJson('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (e) { console.error('[ctx shipping calc] could not restore line', it, e); }
    }

    if (snap.note || snap.attributes) {
      var upd = {};
      if (snap.note) upd.note = snap.note;
      if (snap.attributes) upd.attributes = snap.attributes;
      try {
        await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(upd)
        });
      } catch (e) { console.error('[ctx shipping calc] could not restore note/attributes:', e); }
    }

    /* Anything showing a cart count is now stale - it was emptied and refilled. */
    document.dispatchEvent(new CustomEvent('ctx:cart-restored'));
  }

  /* Recovery for a tab that died mid-quote. Runs once on load. */
  (async function recoverAbandonedSnapshot() {
    var raw = null;
    try { raw = sessionStorage.getItem(SNAPSHOT_KEY); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (e) {}

    var snap;
    try { snap = JSON.parse(raw); } catch (e) { return; }
    if (!snap || !snap.items || !snap.items.length) return;

    /* Only step in if the cart still looks wrong. If the quote finished normally
       the snapshot was already cleared, so reaching here means it did not. */
    var cur = await fetchJson('/cart.js', { headers: { Accept: 'application/json' } });
    var count = (cur.body && cur.body.item_count) || 0;
    if (count === snap.items.length) return;

    log('restoring a cart left behind by an interrupted quote');
    await restoreCart(snap);
  })();

  async function run(opts) {
    var zip = opts.zip;
    var state = opts.state;
    var result = opts.resultEl;
    var btn = opts.btnEl;
    var p = opts.classPrefix || 'pd';

    var variantId = opts.variantId || getVariantId();
    if (!variantId) {
      result.textContent = 'Could not detect this product. Refresh the page and try again.';
      log('FAILED: no variant id found on the page.');
      return;
    }

    result.innerHTML =
      '<span class="' + p + '-loading">' +
        '<span class="' + p + '-spinner" aria-hidden="true"></span>' +
        '<span class="' + p + '-loading-text">Calculating shipping for <strong>' + zip + '</strong>&hellip;</span>' +
        '<span class="' + p + '-loading-note"></span>' +
      '</span>';
    if (btn) btn.disabled = true;

    // If freight is slow, reassure them rather than let them wonder.
    var slowTimer = setTimeout(function () {
      var note = result.querySelector('.' + p + '-loading-note');
      if (note) note.textContent = 'Freight quotes can take a few seconds.';
    }, 5000);

    var qty = opts.qty || getQty();
    var snap = null, emptied = false, hadItems = false;
    /* Only used on the fallback path, where the cart could not be emptied and the
       quote runs against it as-is - then the temporary line has to be wound back
       individually instead of clearing everything. */
    var addedKey = null, priorQty = 0;

    log('START', { variantId: variantId, qty: qty, state: state, zip: zip });

    /* 28 Oct 2026: ask the server-side quote first (n8n "Ship quote (product page)", the same source as
       the delivery bar above the title). It prices this one item with a draft-order calculation, so the
       cart is never touched, and it works for SOLD-OUT items too: Shopify's storefront rate lookup below
       returns an empty list for any cart holding an out-of-stock line, which showed "No shipping options
       returned" on sold-out PDPs while the delivery bar showed a price. Kept outside the try below on
       purpose: that block's cleanup clears the cart when it thinks the cart started empty. */
    try {
      var qRes = await fetch('https://buildagym.app.n8n.cloud/webhook/bag-ship-quote?v=' + encodeURIComponent(variantId) +
        '&q=' + encodeURIComponent(qty) + '&zip=' + encodeURIComponent(zip));
      var qBody = qRes.ok ? await qRes.json() : null;
      if (qBody && qBody.ok && qBody.rates && qBody.rates.length) {
        log('server quote', qBody);
        clearTimeout(slowTimer);
        renderRates(result, qBody.rates.map(function (r) { return { name: r.t, price: String(r.p) }; }), p);
        if (btn) btn.disabled = false;
        return;
      }
      log('server quote empty or failed - falling back to the cart lookup', qBody);
    } catch (eq) {
      log('server quote error - falling back to the cart lookup', eq);
    }

    try {
      /* 1. Snapshot the cart, then empty it so the quote prices THIS item only.

         The customer's own cart must not affect the number on a product page: if
         they left a bench in the cart last week, a treadmill's freight quote that
         silently includes it is worse than no quote at all. */
      var cartRes = await fetchJson('/cart.js', { headers: { Accept: 'application/json' } });

      if (cartRes.body && cartRes.body.items && cartRes.body.items.length) {
        hadItems = true;
        cartRes.body.items.forEach(function (it) {
          if (String(it.variant_id || it.id) === String(variantId)) priorQty = it.quantity;
        });
        snap = snapshotCart(cartRes.body);

        /* Recorded before emptying, so an interrupted quote can be recovered on
           the next load. If sessionStorage is unavailable (private mode, quota)
           the cart is left alone rather than emptied unrecoverably. */
        var stored = false;
        try {
          sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
          stored = true;
        } catch (e) {
          log('no sessionStorage - quoting the cart as-is rather than risk it');
        }

        if (stored) {
          var cleared = await fetchJson('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
          });
          emptied = cleared.ok;
          if (!emptied) {
            snap = null;
            try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (e2) {}
            log('clear failed - falling back to quoting the whole cart');
          }
        } else {
          snap = null;
        }
      }

      // 2. Add this product on its own
      var addRes = await fetchJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });

      if (!addRes.ok) {
        result.textContent = 'Could not price this item. ' + (errorText(addRes.body) || '');
        return;
      }
      addedKey = (addRes.body && (addRes.body.key ||
                 (addRes.body.items && addRes.body.items[0] && addRes.body.items[0].key))) || null;

      // 3. Ask Shopify to calculate. Full names are the documented format.
      var params = new URLSearchParams();
      params.set('shipping_address[country]', 'United States');
      params.set('shipping_address[province]', state);
      params.set('shipping_address[zip]', zip);
      var qs = params.toString();

      var prep = await fetchJson('/cart/prepare_shipping_rates.json?' + qs, { method: 'POST' });
      if (!prep.ok) {
        result.textContent = 'Shopify rejected that address. ' + (errorText(prep.body) || '');
        return;
      }

      // 4. Poll for the result
      var rates = null;
      for (var i = 0; i < POLL_ATTEMPTS; i++) {
        var poll = await fetchJson('/cart/async_shipping_rates.json?' + qs, {
          headers: { Accept: 'application/json' }
        });
        if (!poll.ok) {
          result.textContent = 'Rate lookup failed. ' + (errorText(poll.body) || ('Status ' + poll.status));
          return;
        }
        if (poll.body && poll.body.shipping_rates) { rates = poll.body.shipping_rates; break; }
        await wait(POLL_INTERVAL);
      }

      if (rates === null) {
        result.textContent = 'Rates are taking longer than expected. Please try again.';
        return;
      }

      renderRates(result, rates, p);
    } catch (e) {
      result.textContent = 'Could not calculate shipping right now. Please try again.';
      console.error('[ctx shipping calc] exception:', e);
    } finally {
      clearTimeout(slowTimer);

      /* 5. Put the cart back exactly as it was, whatever happened above.

         Three distinct cases - clearing in the wrong one would delete a real cart:
         a) emptied for the quote  -> replay the snapshot (clear + re-add).
         b) cart was empty already -> clear, which removes only our temp line.
         c) fallback, cart kept    -> wind our line back to its previous quantity,
                                      leaving every other line untouched. */
      try {
        if (emptied && snap) {
          await restoreCart(snap);
        } else if (!hadItems) {
          await fetchJson('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
          });
        } else if (addedKey) {
          await fetchJson('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: addedKey, quantity: priorQty })
          });
        }
      } catch (e2) {
        console.error('[ctx shipping calc] cleanup failed:', e2);
      }

      /* Cleared last, and only after a restore that did not throw - while it is
         present, a reload will rebuild the cart. */
      try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (e3) {}

      if (btn) btn.disabled = false;
    }
  }

  window.ctxShippingCalc = { run: run, money: money };
})();
